import { Branch } from '@/entities/Album'
import FaceEntity from '@/entities/Face'
import EventEntity from '@/entities/Event'
import PhotoEntity from '@/entities/Photo'
import GroupEntity from '@/entities/Group'
import ChildEntity from '@/entities/Child'
import ProfileEntity from '@/entities/Profile'
import CartQueryEntity from '@/entities/C4'
import EventRepository from '@/repositories/EventRepository'
import GroupRepository from '@/repositories/GroupRepository'
import MetaRepository from '@/repositories/MetaRepository'
import ProfileRepository from '@/repositories/ProfileRepository'
import { pluck, pipe, curriedPluck } from '@/utils/helper'

export interface IPresenterParams {
  eventRepository: EventRepository
  metaRepository: MetaRepository
  groupRepository: GroupRepository
  profileRepository: ProfileRepository
  eventId: number
}

export interface IPresenter {
  albumsTree: Branch[]
  event: EventEntity | null
  uchinokoFaces: FaceEntity[]
  firstUchinokoFaceImageUrl: string | null
  uchinokoPhotosByAlbums: {
    [albumId: number]: {
      [photoId: number]: PhotoEntity
    }
  }
  uchinokoPhotosByAlbumsCount: number
  hasNoSearchResult: boolean
  latestServerTimestamp: string | null
  isUchinokoSearchEnabled: boolean
  adminGroup: GroupEntity | null
  childrenList: ChildEntity[]
  hasAdminPrivilege: boolean // 管理者としての閲覧権限がある
  profile: ProfileEntity | null
  groupId: number | undefined
  cartQueryStatus: CartQueryEntity | null
  hasThumbnail: boolean
  isProfileSearchEnabled: boolean
  isProfileImageRegisteredNotProfileSearchEnable: boolean
}

export default ({ eventRepository, metaRepository, groupRepository, profileRepository, eventId }: IPresenterParams): IPresenter => {
  const selectedUchinokoFaces = eventRepository.getSelectedUchinokoFaces(eventId)
  const selectedUchinokoFacesCount = Object.keys(selectedUchinokoFaces).filter(faceId => selectedUchinokoFaces[faceId]).length
  const uchinokoPhotosByAlbumsCount = eventRepository.getUchinokoEventPhotosByAlbumsCount(eventId)
  const groups = groupRepository.getGroupsByEvent({ eventId })
  const groupSettings = groupRepository.getGroupSettings(pluck(pluck(groups, 'props'), 'id'))
  const cartQueryStatus = eventRepository.getCartQueryStatus(eventId)

  // 購入履歴からかお検索結果のかお写真データ取得
  const uchinokoFaces = eventRepository.getUchiokoFaces(eventId)
  let childrenList: ChildEntity[] = []

  const children = groupRepository.getChildrenByEvent(eventId)
  childrenList = Object.values(children)
    .reduce((acc, ac) => [...acc, ...ac], [] as ChildEntity[])
    .filter(child => child.props.profileImage)
  const hasThumbnail = childrenList.some((child: ChildEntity) => {
    return child.props.profileImage
  })

  // 初回かお検索結果のかお写真データ
  let firstUchinokoFaceImageUrl: string | null = null
  if (uchinokoFaces.length && cartQueryStatus && cartQueryStatus.props.hasCartQuery && !cartQueryStatus.props.hasOrderHistory) {
    firstUchinokoFaceImageUrl = uchinokoFaces[0].props.imageUrl
  }

  let hasAdminPrivilege = false
  const adminGroup = groupRepository.getAdminGroup()
  if (adminGroup) {
    const adminSchools = groupRepository.getSchools(adminGroup.props.id)
    hasAdminPrivilege = adminSchools.some(school => school.props.eventIds.includes(eventId))
  }

  const profile = profileRepository.getProfile()

  // groupIdの取得
  let groupId: number
  if (adminGroup) {
    groupId = adminGroup.props.id
  } else {
    const groupInfo = groupRepository.getGroups()
    groupId = groupInfo[0] && groupInfo[0].props.id
  }

  const targetGroups = groupRepository.getGroupsByEvent({ eventId })
  const settings = groupRepository.getGroupSettings(pluck(pluck(targetGroups, 'props'), 'id'))
  const childIds = pipe(() => childrenList, curriedPluck('props'), curriedPluck('id'))
  const profileSearchEnabledChildren = settings.map(setting => {
    return setting.children
      .map(children => {
        if (children.is_profile_image_search_enabled) return children.child_id
      })
      .filter((n): n is number => {
        return typeof n === 'number'
      })
  })

  const isProfileSearchEnabled = profileSearchEnabledChildren
    .map(children => [...children, ...childIds].filter(NumbersToCompare => children.includes(NumbersToCompare) && childIds.includes(NumbersToCompare)).length > 0)
    .some(bool => bool === true)

  let isProfileImageRegisteredNotProfileSearchEnable: boolean
  if (adminGroup && children[adminGroup.props.id]) {
    const adminGroupHasProfileImageChildren = children[adminGroup.props.id].filter(child => child.props.profileImage)
    const adminGroupHasProfileImageChildIds = pipe(() => adminGroupHasProfileImageChildren, curriedPluck('props'), curriedPluck('id'))
    isProfileImageRegisteredNotProfileSearchEnable = !isProfileSearchEnabled && !!adminGroupHasProfileImageChildIds.length
  } else {
    isProfileImageRegisteredNotProfileSearchEnable = false
  }

  return {
    albumsTree: eventRepository.getAlbumsTree(eventId),
    event: eventRepository.getEvent(eventId),
    uchinokoFaces,
    firstUchinokoFaceImageUrl,
    uchinokoPhotosByAlbums: eventRepository.getUchinokoEventPhotosByAlbums(eventId),
    uchinokoPhotosByAlbumsCount,
    hasNoSearchResult: uchinokoPhotosByAlbumsCount === 0 && selectedUchinokoFacesCount === Object.keys(selectedUchinokoFaces).length,
    latestServerTimestamp: metaRepository.getLatestServerTimestamp(),
    isUchinokoSearchEnabled: groupSettings.some(setting => setting.isUchinokoSearchEnabled),
    adminGroup: groupRepository.getAdminGroup(),
    childrenList,
    hasAdminPrivilege,
    profile,
    groupId,
    cartQueryStatus,
    hasThumbnail,
    isProfileSearchEnabled,
    isProfileImageRegisteredNotProfileSearchEnable
  }
}
